import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import {
    GoogleMap,
    Polygon,
    useLoadScript,
} from "@react-google-maps/api";
import "../../Assets/Styles/RobotTaskFields.css";

const mapContainerStyle = { width: "100%", height: "350px" };
const defaultCenter = { lat: 20.5937, lng: 78.9629 }; // India center

export const RobotTaskFields = ({ task, onClose, onSubmit }) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey:
            process.env.REACT_APP_GOOGLE_API_KEY ||
            "AIzaSyDmmBMzFbZmGwc3FMR90OQNy8K3WFFK9eU",
        libraries: ["places", "geometry"],
    });

    const [form, setForm] = useState({
        task_id: 0,
        task_name: "",
        farmer_id: "",
        farm_id: "",
        field_id: "",
        robot_id: "",
        boundry_coordinates: "[]",
        task_type: "",
        task_priority: "Low",
        start_date: "",
        end_date: "",
        status: "Scheduled",
        area_hectare: "",
        user_id: 1,
    });

    const [polygonPath, setPolygonPath] = useState([]);
    const [areaHectare, setAreaHectare] = useState(0);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [searchInput, setSearchInput] = useState("");

    const mapRef = useRef(null);

    // 🧭 Load data from props
    useEffect(() => {
        if (task) {
            setForm({
                task_id: task.task_id || 0,
                task_name: task.task_name || "",
                farmer_id: task.farmer_id || "",
                farm_id: task.farm_id || "",
                field_id: task.field_id || "",
                robot_id: task.robot_id || "",
                boundry_coordinates: task.boundry_coordinates || "[]",
                task_type: task.task_type || "",
                task_priority: task.task_priority || "Normal",
                start_date: task.start_date
                    ? moment(task.start_date).format("YYYY-MM-DDTHH:mm")
                    : "",
                end_date: task.end_date
                    ? moment(task.end_date).format("YYYY-MM-DDTHH:mm")
                    : "",
                status: task.status || "Scheduled",
                area_hectare: task.area_hectare || "",
                user_id: task.user_id || 1,
            });

            try {
                const coords = JSON.parse(task.boundry_coordinates || "[]");
                setPolygonPath(coords);
            } catch {
                setPolygonPath([]);
            }
        }
    }, [task]);

    // 🧮 Calculate area
    const calculateArea = (path) => {
        if (!path || path.length < 3) return 0;
        if (!window.google?.maps?.geometry) return 0;
        const googlePath = path.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
        const areaM2 = window.google.maps.geometry.spherical.computeArea(googlePath);
        return areaM2 / 10000;
    };

    useEffect(() => {
        const area = calculateArea(polygonPath);
        setAreaHectare(area);
    }, [polygonPath]);

    // 📝 Input change handler
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // 🗺️ Map click — add polygon point
    const handleMapClick = (e) => {
        const newPath = [...polygonPath, { lat: e.latLng.lat(), lng: e.latLng.lng() }];
        setPolygonPath(newPath);
    };

    // 🔍 Search place or coordinates
    const handleSearch = async () => {
        if (!searchInput.trim()) return;

        // Check if user entered coordinates
        const coordMatch = searchInput.match(
            /^([-+]?\d{1,2}\.\d+)\s*,\s*([-+]?\d{1,3}\.\d+)$/
        );
        if (coordMatch) {
            const lat = parseFloat(coordMatch[1]);
            const lng = parseFloat(coordMatch[2]);
            const newCenter = { lat, lng };
            setMapCenter(newCenter);
            mapRef.current.panTo(newCenter);
            return;
        }

        // Otherwise, search by place name
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: searchInput }, (results, status) => {
            if (status === "OK" && results[0]) {
                const location = results[0].geometry.location;
                const newCenter = { lat: location.lat(), lng: location.lng() };
                setMapCenter(newCenter);
                mapRef.current.panTo(newCenter);
            } else {
                alert("Place not found. Please try another location.");
            }
        });
    };

    // 💾 Submit form
    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            start_date: new Date(form.start_date),
            end_date: new Date(form.end_date),
            boundry_coordinates: JSON.stringify(polygonPath),
            area_hectare: areaHectare.toFixed(2),
        };
        onSubmit(payload);
    };

    if (!isLoaded) return <div>Loading map...</div>;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h3>{form.task_id ? "Edit Task Fields" : "Add Task Fields"}</h3>

                <form onSubmit={handleSubmit} className="task-fields-form">
                    <div className="form-section">
                        <input
                            name="task_name"
                            value={form.task_name}
                            onChange={handleChange}
                            placeholder="Task Name"
                            required
                        />
                        <input
                            name="task_type"
                            value={form.task_type}
                            onChange={handleChange}
                            placeholder="Task Type"
                            required
                        />

                        <input
                            type="datetime-local"
                            name="start_date"
                            value={form.start_date}
                            onChange={handleChange}
                            required
                        />
                        <input
                            type="datetime-local"
                            name="end_date"
                            value={form.end_date}
                            onChange={handleChange}
                            required
                        />

                        <select name="status" value={form.status} onChange={handleChange}>
                            <option value="Scheduled">Scheduled</option>
                            <option value="InProgress">In Progress</option>
                            <option value="Done">Done</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>

                        <select name="task_priority" value={form.task_priority} onChange={handleChange}>
                            <option value="Low">Low</option>
                            <option value="Normal">Normal</option>
                            <option value="High">High</option>
                        </select>

                        <input
                            name="robot_id"
                            value={form.robot_id}
                            onChange={handleChange}
                            placeholder="Robot ID"
                        />
                        <input
                            name="farmer_id"
                            value={form.farmer_id}
                            onChange={handleChange}
                            placeholder="Farmer ID"
                        />
                        <input
                            name="farm_id"
                            value={form.farm_id}
                            onChange={handleChange}
                            placeholder="Farm ID"
                        />
                        <input
                            name="field_id"
                            value={form.field_id}
                            onChange={handleChange}
                            placeholder="Field ID"
                        />

                        <div style={{ display: "flex", gap: "5px" }}>
                            <input
                                type="text"
                                placeholder="Enter place name or lat,lng"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="map-search-box"
                            />
                            <button
                                type="button"
                                className="search-btn"
                                onClick={handleSearch}
                            >
                                Search
                            </button>
                        </div>

                        <p className="area-info">Field Area: {areaHectare.toFixed(2)} ha</p>

                        <div className="button-group">
                            <button type="submit">Save Task Fields</button>
                            <button type="button" onClick={onClose}>
                                Cancel
                            </button>
                        </div>
                    </div>

                    <div className="map-section">
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={6}
                            onClick={handleMapClick}
                            onLoad={(map) => (mapRef.current = map)}
                        >
                            {polygonPath.length > 0 && (
                                <Polygon
                                    path={polygonPath}
                                    options={{
                                        fillColor: "lightgreen",
                                        fillOpacity: 0.4,
                                        strokeColor: "green",
                                        strokeWeight: 2,
                                    }}
                                />
                            )}
                        </GoogleMap>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RobotTaskFields;







//import React, { useState, useEffect, useRef } from "react";
//import moment from "moment";
//import {
//    GoogleMap,
//    Polygon,
//    useLoadScript,
//    StandaloneSearchBox,
//} from "@react-google-maps/api";
//import "../../Assets/Styles/RobotTaskFields.css";

//const mapContainerStyle = { width: "100%", height: "350px" };
//const defaultCenter = { lat: 20.5937, lng: 78.9629 }; // India center

//export const RobotTaskFields = ({ task, onClose, onSubmit }) => {
//    const { isLoaded } = useLoadScript({
//        googleMapsApiKey:
//            process.env.REACT_APP_GOOGLE_API_KEY ||
//            "AIzaSyDmmBMzFbZmGwc3FMR90OQNy8K3WFFK9eU",
//        libraries: ["places", "geometry"],
//    });

//    const [form, setForm] = useState({
//        task_id: task?.task_id || 0,
//        task_name: task?.task_name || "",
//        farmer_id: task?.farmer_id || "",
//        farm_id: task?.farm_id || "",
//        field_id: task?.field_id || "",
//        robot_id: task?.robot_id || "",
//        boundry_coordinates: task?.boundry_coordinates || "",
//        task_type: task?.task_type || "",
//        task_priority: task?.task_priority || "1",
//        start_date: task?.start_date
//            ? moment(task.start_date).format("YYYY-MM-DDTHH:mm")
//            : "",
//        end_date: task?.end_date
//            ? moment(task.end_date).format("YYYY-MM-DDTHH:mm")
//            : "",
//        status: task?.status || "",
//        area_hectare: task?.area_hectare || "",
//        user_id: task?.user_id || 1,
//    });

//    const [polygonPath, setPolygonPath] = useState(
//        task?.boundary_coordinates ? JSON.parse(task.boundary_coordinates) : []
//    );

//    const [areaHectare, setAreaHectare] = useState(0);
//    const [mapCenter, setMapCenter] = useState(defaultCenter);

//    const mapRef = useRef(null);
//    const searchBoxRef = useRef(null);

//    // 🧮 Calculate area
//    const calculateArea = (path) => {
//        if (!path || path.length < 3) return 0;
//        if (!window.google?.maps?.geometry) return 0;
//        const googlePath = path.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
//        const areaM2 = window.google.maps.geometry.spherical.computeArea(googlePath);
//        return areaM2 / 10000;
//    };

//    useEffect(() => {
//        setAreaHectare(calculateArea(polygonPath));
//    }, [polygonPath]);

//    // 📝 Input change handler
//    const handleChange = (e) => {
//        const { name, value } = e.target;
//        setForm((prev) => ({ ...prev, [name]: value }));
//    };

//    // 🗺️ Add polygon point on map click
//    const handleMapClick = (e) => {
//        setPolygonPath([...polygonPath, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
//    };

//    // 📍 Place search
//    const handlePlaceChanged = () => {
//        const places = searchBoxRef.current.getPlaces();
//        if (places?.length > 0) {
//            const location = places[0].geometry.location;
//            const newCenter = { lat: location.lat(), lng: location.lng() };
//            setMapCenter(newCenter);
//            mapRef.current.panTo(newCenter);
//        }
//    };

//    // 💾 Submit form
//    const handleSubmit = (e) => {
//        e.preventDefault();
//        const payload = {
//            ...form,
//            start_date: new Date(form.start_date),
//            end_date: new Date(form.end_date),
//           // boundary_coordinates: JSON.stringify(polygonPath),
//           // area_hectare: areaHectare,
//        };
//        onSubmit(payload);
//    };

//    if (!isLoaded) return <div>Loading map...</div>;

//    return (
//        <div className="modal-overlay">
//            <div className="modal-content">
//                <h3>{form.task_id ? "Edit Task Fields" : "Add Task Fields"}</h3>

//                <form onSubmit={handleSubmit} className="task-fields-form">
//                    <div className="form-section">
//                        <input
//                            name="task_name"
//                            value={form.task_name}
//                            onChange={handleChange}
//                            placeholder="Task Name"
//                            required
//                        />
//                        <input
//                            name="task_type"
//                            value={form.task_type}
//                            onChange={handleChange}
//                            placeholder="Task Type"
//                            required
//                        />

//                        <input
//                            type="datetime-local"
//                            name="start_date"
//                            value={form.start_date}
//                            onChange={handleChange}
//                            required
//                        />

//                        <input
//                            type="datetime-local"
//                            name="end_date"
//                            value={form.end_date}
//                            onChange={handleChange}
//                            required
//                        />

//                        <select name="status" value={form.status} onChange={handleChange}>
//                            <option value="Scheduled">Scheduled</option>
//                            <option value="InProgress">In Progress</option>
//                            <option value="Done">Done</option>
//                            <option value="Cancelled">Cancelled</option>
//                        </select>

//                        <select name="task_priority" value={form.task_priority} onChange={handleChange}>
//                            <option value="Low">Low</option>
//                            <option value="Normal">Normal</option>
//                            <option value="High">High</option>
//                        </select>

//                        <input
//                            name="robot_id"
//                            value={form.robot_id}
//                            onChange={handleChange}
//                            placeholder="Robot ID"
//                        />
//                        <input
//                            name="farmer_id"
//                            value={form.farmer_id}
//                            onChange={handleChange}
//                            placeholder="Farmer ID"
//                        />
//                        <input
//                            name="farm_id"
//                            value={form.farm_id}
//                            onChange={handleChange}
//                            placeholder="Farm ID"
//                        />
//                        <input
//                            name="field_id"
//                            value={form.field_id}
//                            onChange={handleChange}
//                            placeholder="Field ID"
//                        />

//                        <p className="area-info">Field Area: {areaHectare.toFixed(2)} ha</p>

//                        <div className="button-group">
//                            <button type="submit">Save Task Fields</button>
//                            <button type="button" onClick={onClose}>
//                                Cancel
//                            </button>
//                        </div>
//                    </div>

//                    <div className="map-section">
//                        <StandaloneSearchBox
//                            onLoad={(ref) => (searchBoxRef.current = ref)}
//                            onPlacesChanged={handlePlaceChanged}
//                        >
//                            <input
//                                type="text"
//                                placeholder="Search place or coordinates..."
//                                className="map-search-box"
//                            />
//                        </StandaloneSearchBox>

//                        <GoogleMap
//                            mapContainerStyle={mapContainerStyle}
//                            center={mapCenter}
//                            zoom={6}
//                            onClick={handleMapClick}
//                            onLoad={(map) => (mapRef.current = map)}
//                        >
//                            {polygonPath.length > 0 && (
//                                <Polygon
//                                    path={polygonPath}
//                                    options={{
//                                        fillColor: "lightgreen",
//                                        fillOpacity: 0.4,
//                                        strokeColor: "green",
//                                        strokeWeight: 2,
//                                    }}
//                                />
//                            )}
//                        </GoogleMap>
//                    </div>
//                </form>
//            </div>
//        </div>
//    );
//};

//export default RobotTaskFields;
